import {isFeatureEnabled} from 'feature-flags';
import {Separator} from 'platform/components';
import {Box, getValueByDevice, GridItem, useDevice, VStack} from 'platform/foundation';

import {isNilOrEmpty} from 'ramda-adjunct';

import {useGetParticipationQuery} from '@dms/api/core';
import {EntityResourceIds} from '@dms/api/shared';
import {useGetSaleVehicleQuery} from '@dms/api/vehicle';
import {featureFlags} from '@dms/feature-flags';
import {
  VehicleForm as VFKit,
  VehicleSubSections as VFSubSection,
  VehicleFieldGroups as VFGroup,
  VehicleFormDefaultLayout,
  isElectricOrHybridFuelType,
  useVehicleFormContext,
} from '@dms/features/vehicle-forms-kit';
import i18n from '@dms/i18n';
import {useIsQualitySealVisible, usePermissions} from '@dms/shared';

import {useRequiredParams} from 'shared/web';

import {AdditionalInfoWidget} from '../components/AdditionalInfoWidget';

export function VehicleData() {
  const {id: vehicleId} = useRequiredParams();
  const {vehicleVariant} = useVehicleFormContext();

  const device = useDevice();
  const isExpandable = getValueByDevice(device, true, true, true, false);

  const {data: vehicleParticipation} = useGetParticipationQuery(
    {
      resourceId: EntityResourceIds.vehicle,
      recordId: vehicleId,
    },
    {skip: isNilOrEmpty(vehicleId)}
  );

  const {data: saleVehicle} = useGetSaleVehicleQuery({vehicleId}, {skip: isNilOrEmpty(vehicleId)});

  // Drives the warranty fields' behaviour: while a preset-resolved program is set both fields are
  // locked; once the program is cleared they become editable and fall back to the per-vehicle
  // default warranty duration.
  const isWarrantyProgramResolvedFromSalePreset =
    saleVehicle?.isWarrantyProgramResolvedFromSalePreset ?? false;

  // Campaign-resolved warranty is owned by the campaign: both warranty fields lock and a preset
  // change must not overwrite the campaign values. Only ever set on the edit form.
  const isWarrantyProgramResolvedFromCampaign =
    saleVehicle?.isWarrantyProgramResolvedFromCampaign ?? false;

  // The warranty fields are rendered for sale (non-rental) vehicles when SALES_WARRANTY is on;
  // only then should selecting a preset resolve the warranty program.
  const isWarrantyShown =
    !vehicleVariant?.hasRentalVehicle && isFeatureEnabled(featureFlags.SALES_WARRANTY);

  const [canReadAdvertisingSettings] = usePermissions({
    permissionKeys: ['readAdvertisingSettings'],
    scopes: {
      readAdvertisingSettings: {
        participation: vehicleParticipation,
        branchId: saleVehicle?.branchId,
      },
    },
  });
  const isQualitySealShown = useIsQualitySealVisible() && canReadAdvertisingSettings;
  const warrantyFields = (
    <VFKit.Suspense>
      <VFKit.WarrantyProgramSelect
        isWarrantyProgramResolvedFromSalePreset={isWarrantyProgramResolvedFromSalePreset}
        isWarrantyProgramResolvedFromCampaign={isWarrantyProgramResolvedFromCampaign}
      />
      <VFKit.WarrantyDurationField
        isWarrantyProgramResolvedFromSalePreset={isWarrantyProgramResolvedFromSalePreset}
        isWarrantyProgramResolvedFromCampaign={isWarrantyProgramResolvedFromCampaign}
      />
    </VFKit.Suspense>
  );

  return (
    <VehicleFormDefaultLayout>
      <VehicleFormDefaultLayout.Left>
        <VStack spacing={4}>
          <VFKit.Section
            data-testid="vehicle-data"
            isExpandable={isExpandable}
            title={i18n.t('entity.vehicle.labels.vehicleData')}
          >
            <VFKit.ShowWhen vehicleVariants={['sale']}>
              <VFKit.ConditionChips />
              <VFKit.Group data-testid="mileage-fields" columns={[1, 2, 4]}>
                <VFKit.SimpleMileageField name="mileage" vehicleId={vehicleId} />
                <VFKit.RealMileageInput />
                <VFGroup.Ownership />
              </VFKit.Group>
              <VFKit.Group data-testid="country-fields" columns={[1, 2, 4]}>
                <VFGroup.CountryOfOrigin />
              </VFKit.Group>
              <VFKit.Group data-testid="source-select" columns={[1, 2, 4]}>
                <VFKit.SourceSelect />
                <VFKit.ShowWhen
                  when={canReadAdvertisingSettings}
                  whenFeatureEnabled={featureFlags.SALES_SALE_PROGRAMS}
                  vehicleVariants={['sale']}
                >
                  <VFKit.SalePresetSelect
                    isWarrantyShown={isWarrantyShown}
                    isQualitySealShown={isQualitySealShown}
                    isWarrantyProgramResolvedFromCampaign={isWarrantyProgramResolvedFromCampaign}
                  />
                </VFKit.ShowWhen>
                <VFKit.ShowWhen when={isQualitySealShown}>
                  <VFKit.Suspense>
                    <VFKit.QualitySealSelect selectedQualitySeal={saleVehicle?.qualitySeal} />
                  </VFKit.Suspense>
                </VFKit.ShowWhen>
                <VFKit.ShowWhen
                  vehicleVariants={['sale']}
                  when={!isQualitySealShown && !vehicleVariant?.hasRentalVehicle}
                  whenFeatureEnabled={featureFlags.SALES_WARRANTY}
                >
                  {warrantyFields}
                </VFKit.ShowWhen>
              </VFKit.Group>
              <VFKit.ShowWhen
                vehicleVariants={['sale']}
                when={isQualitySealShown && !vehicleVariant?.hasRentalVehicle}
                whenFeatureEnabled={featureFlags.SALES_WARRANTY}
              >
                <VFKit.Group data-testid="warranty-fields" columns={[1, 2, 4]}>
                  {warrantyFields}
                </VFKit.Group>
              </VFKit.ShowWhen>
              <Separator spacing={1} />
              <VFKit.Group data-testid="registration-row" columns={[1, 2, 4]}>
                <GridItem span={[1, 2, 2]}>
                  <VFKit.Group data-testid="registration-numbers">
                    <VFGroup.RegistrationNumber />
                  </VFKit.Group>
                </GridItem>
                <VFKit.ServicedInAuthorizedNetworkChips />
              </VFKit.Group>
              <VFKit.Group data-testid="registration-checkboxes" columns={[1, 2, 4]}>
                <VFKit.IsRegisteredCheckbox />
                <VFKit.HasCocCheckbox />
              </VFKit.Group>
            </VFKit.ShowWhen>
            <VFKit.OtherRecordsTextarea />
            <VFKit.ShowWhen
              name="fuelType"
              condition={(fuelType) => isElectricOrHybridFuelType(fuelType?.[0])}
            >
              <VFSubSection.BatteryHealth />
            </VFKit.ShowWhen>
            <VFKit.SubSection
              data-testid="warranty-section"
              title={i18n.t('entity.vehicle.labels.warrantyAndValidity')}
              useCard
            >
              <VFKit.Group data-testid="warranty-fields" columns={[1, 2, 4]}>
                <VFKit.WarrantyDatePicker />
                <VFKit.SimpleNumericField name="warrantyMileage" />
                <VFKit.ServiceContractUntilDatePicker />
                <VFKit.SimpleNumericField name="serviceContractMileageLimit" />
              </VFKit.Group>
              <VFKit.ServiceContractNoteTextarea />
            </VFKit.SubSection>
            <VFKit.SubSection
              data-testid="technical-inspection-section"
              title={i18n.t('entity.vehicle.labels.technicalInspection')}
              useCard
            >
              <VFKit.Group data-testid="technical-inspection-fields" columns={[2, 2, 2, 3]}>
                <VFKit.TechnicalInspectionValidUntilDatePicker />
                <Box paddingTop={5} flexGrow={1}>
                  <VFKit.IsAcceptableForTechnicalInspectionCheckbox />
                </Box>
              </VFKit.Group>
              <VFKit.TechnicalInspectionNoteTextarea />
            </VFKit.SubSection>
            <VFKit.SubSection
              data-testid="service-book-section"
              title={i18n.t('entity.vehicle.labels.serviceBook')}
              useCard
            >
              <VFKit.Group data-testid="service-book-type" columns={[1, 2]}>
                <VFKit.ServiceBookTypeChips />
                <VFKit.ShowWhen
                  name="serviceBookType"
                  condition={(serviceBookType) => serviceBookType?.at(0) !== 'SERVICEBOOKTYPE_NONE'}
                >
                  <VFKit.ServiceBookStateChips />
                </VFKit.ShowWhen>
              </VFKit.Group>
            </VFKit.SubSection>
            <VFKit.ShowWhen vehicleVariants={['sale', 'service']}>
              <VFSubSection.ServiceInspection />
            </VFKit.ShowWhen>
          </VFKit.Section>
        </VStack>
      </VehicleFormDefaultLayout.Left>
      <VehicleFormDefaultLayout.Right>
        <VFKit.SubSection data-testid="additional-info">
          <AdditionalInfoWidget />
        </VFKit.SubSection>
      </VehicleFormDefaultLayout.Right>
    </VehicleFormDefaultLayout>
  );
}
